import React, { useEffect, useId, useMemo, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import DeliveryStatusMultiSelect from '../Deliveries/DeliveryStatusMultiSelect';
import RouteCustomerFilter from '../Rotas/RouteCustomerFilter';
import ReportCostCenterFilter from './ReportCostCenterFilter';
import ReportDriverFilter from './ReportDriverFilter';
import {
    applyReportPeriodLimitHint,
    clearDependentValues,
    getDefaultFilterValues,
    validateFilterValues,
} from './reportFilterUtils';
import {
    Overlay,
    ModalBox,
    ModalHeader,
    ModalTitle,
    ModalDescription,
    ModalForm,
    ModalBody,
    ModalFooter,
    FilterField,
    FieldHint,
    RadioGroup,
    RadioOption,
    FormError,
} from './styles';

const { Button } = CrudStyles;
const EMPTY_FILTERS = [];

function ReportFilterField({
    filter,
    value,
    values,
    onChange,
    onValuesChange,
    error,
    idPrefix,
    disabled = false,
}) {
    const fieldId = `${idPrefix}-${filter.name}`;
    const hasError = Boolean(error);

    if (filter.type === 'customer') {
        const displayNameKey = filter.displayNameKey || 'customerName';
        return (
            <FilterField $hasError={hasError}>
                <RouteCustomerFilter
                    label={filter.label}
                    inputId={fieldId}
                    selectedCustomerId={value}
                    selectedCustomerName={values?.[displayNameKey] || ''}
                    disabled={disabled}
                    dropdownZIndex={10050}
                    includePhysicalCustomers
                    onSelect={(customer) => {
                        onValuesChange?.({
                            [filter.name]: customer?.id ?? '',
                            [displayNameKey]: customer?.name ?? '',
                        });
                    }}
                />
                {(hasError || filter.hint) && (
                    <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
                )}
            </FilterField>
        );
    }

    if (filter.type === 'driver') {
        const displayNameKey = filter.displayNameKey || 'driverName';
        return (
            <ReportDriverFilter
                label={filter.label}
                driverId={value}
                driverName={values?.[displayNameKey] || ''}
                disabled={disabled}
                error={error}
                hint={filter.hint}
                modalZIndex={10050}
                onSelect={(driver) => {
                    onValuesChange?.({
                        [filter.name]: driver?.id ?? '',
                        [displayNameKey]: driver?.name ?? '',
                    });
                }}
            />
        );
    }

    if (filter.type === 'costCenter') {
        const displayNameKey = filter.displayNameKey || 'costCenterName';
        return (
            <ReportCostCenterFilter
                label={filter.label}
                customerId={values?.[filter.dependsOn || 'customerId'] || ''}
                costCenterId={value}
                costCenterName={values?.[displayNameKey] || ''}
                disabled={disabled}
                error={error}
                hint={filter.hint}
                modalZIndex={10050}
                onSelect={(costCenter) => {
                    onValuesChange?.({
                        [filter.name]: costCenter?.id ?? '',
                        [displayNameKey]: costCenter?.name ?? '',
                    });
                }}
            />
        );
    }

    if (filter.type === 'radio') {
        return (
            <FilterField $hasError={hasError}>
                <span id={`${fieldId}-label`}>{filter.label}</span>
                <RadioGroup role="radiogroup" aria-labelledby={`${fieldId}-label`}>
                    {(filter.options || []).map((option) => (
                        <RadioOption
                            key={option.value}
                            $selected={value === option.value}
                        >
                            <input
                                type="radio"
                                name={filter.name}
                                value={option.value}
                                checked={value === option.value}
                                disabled={disabled}
                                onChange={() => onChange(filter.name, option.value)}
                            />
                            {option.label}
                        </RadioOption>
                    ))}
                </RadioGroup>
                {(hasError || filter.hint) && (
                    <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
                )}
            </FilterField>
        );
    }

    if (filter.type === 'multiselect') {
        return (
            <FilterField $hasError={hasError}>
                <DeliveryStatusMultiSelect
                    id={fieldId}
                    label={filter.label}
                    value={Array.isArray(value) ? value : []}
                    options={filter.options || []}
                    disabled={disabled}
                    onChange={(selected) => onChange(filter.name, selected)}
                />
                {(hasError || filter.hint) && (
                    <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
                )}
            </FilterField>
        );
    }

    if (filter.type === 'select') {
        return (
            <FilterField $hasError={hasError}>
                <label htmlFor={fieldId}>{filter.label}</label>
                <select
                    id={fieldId}
                    name={filter.name}
                    value={value ?? ''}
                    disabled={disabled}
                    onChange={(event) => onChange(filter.name, event.target.value)}
                    aria-invalid={hasError}
                >
                    <option value="">{filter.placeholder || 'Todos'}</option>
                    {(filter.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {(hasError || filter.hint) && (
                    <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
                )}
            </FilterField>
        );
    }

    if (filter.type === 'checkbox') {
        return (
            <FilterField $hasError={hasError}>
                <RadioOption $selected={Boolean(value)} as="label">
                    <input
                        id={fieldId}
                        type="checkbox"
                        name={filter.name}
                        checked={Boolean(value)}
                        disabled={disabled}
                        onChange={(event) => onChange(filter.name, event.target.checked)}
                    />
                    {filter.label}
                </RadioOption>
                {(hasError || filter.hint) && (
                    <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
                )}
            </FilterField>
        );
    }

    const inputType = filter.type === 'number' ? 'number' : filter.type === 'date' ? 'date' : 'text';

    return (
        <FilterField $hasError={hasError}>
            <label htmlFor={fieldId}>{filter.label}</label>
            <input
                id={fieldId}
                type={inputType}
                name={filter.name}
                value={value ?? ''}
                placeholder={filter.placeholder}
                min={filter.min}
                max={filter.max}
                disabled={disabled}
                autoComplete="off"
                onChange={(event) => onChange(filter.name, event.target.value)}
                aria-invalid={hasError}
            />
            {(hasError || filter.hint) && (
                <FieldHint $error={hasError}>{error || filter.hint}</FieldHint>
            )}
        </FilterField>
    );
}

/**
 * Modal genérico de filtros.
 * Recebe um schema (`filters`) e renderiza os campos; o relatório só informa o que precisa.
 *
 * Também aceita `children` para filtros customizados que ainda não cabem no schema.
 */
export default function ReportFiltersModal({
    open,
    title = 'Filtros',
    description,
    filters = EMPTY_FILTERS,
    initialValues,
    onClose,
    onSubmit,
    submitLabel = 'Gerar relatório',
    cancelLabel = 'Cancelar',
    submitting = false,
    error,
    maxPeriodDays,
    children,
}) {
    const idPrefix = useId();
    const [values, setValues] = useState(() => getDefaultFilterValues(filters, initialValues));
    const [fieldError, setFieldError] = useState(null);

    const filterKey = useMemo(
        () => filters.map((filter) => filter.name).join('|'),
        [filters]
    );

    const displayedFilters = useMemo(
        () => applyReportPeriodLimitHint(filters, maxPeriodDays),
        [filters, maxPeriodDays]
    );

    useEffect(() => {
        if (!open) return;
        setValues(getDefaultFilterValues(filters, initialValues));
        setFieldError(null);
    }, [open, filterKey, filters, initialValues]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !submitting) onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, submitting, onClose]);

    if (!open) return null;

    const clearFieldErrorForKeys = (keys) => {
        if (fieldError?.field && keys.includes(fieldError.field)) {
            setFieldError(null);
        }
    };

    const handleChange = (name, value) => {
        setValues((prev) => clearDependentValues({ ...prev, [name]: value }, [name], filters));
        clearFieldErrorForKeys([name]);
    };

    const handleValuesChange = (patch) => {
        const changedNames = Object.keys(patch);
        setValues((prev) => clearDependentValues({ ...prev, ...patch }, changedNames, filters));
        clearFieldErrorForKeys(changedNames);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const validation = validateFilterValues(filters, values, { maxDays: maxPeriodDays });
        if (!validation.valid) {
            setFieldError(validation);
            return;
        }
        setFieldError(null);
        onSubmit?.(values);
    };

    return (
        <Overlay
            role="presentation"
            onClick={(event) => {
                if (event.target === event.currentTarget && !submitting) onClose?.();
            }}
        >
            <ModalBox
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${idPrefix}-title`}
                onClick={(event) => event.stopPropagation()}
            >
                <ModalForm onSubmit={handleSubmit} noValidate>
                    <ModalHeader>
                        <ModalTitle id={`${idPrefix}-title`}>{title}</ModalTitle>
                        {description ? (
                            <ModalDescription>{description}</ModalDescription>
                        ) : null}
                    </ModalHeader>

                    <ModalBody>
                        {displayedFilters.map((filter) => (
                            <ReportFilterField
                                key={filter.name}
                                filter={filter}
                                value={values[filter.name]}
                                values={values}
                                onChange={handleChange}
                                onValuesChange={handleValuesChange}
                                error={fieldError?.field === filter.name ? fieldError.message : null}
                                idPrefix={idPrefix}
                                disabled={submitting}
                            />
                        ))}
                        {children}
                        {error ? <FormError>{error}</FormError> : null}
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {cancelLabel}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? 'Gerando...' : submitLabel}
                        </Button>
                    </ModalFooter>
                </ModalForm>
            </ModalBox>
        </Overlay>
    );
}
